import PageLayout from "@/components/feature/PageLayout";
import ScrollReveal from "@/components/base/ScrollReveal";

const issues = [
  "Communication breakdown",
  "Recurring conflict and arguments",
  "Emotional disconnection",
  "Trust and betrayal recovery",
  "Life transitions and stress",
  "Intimacy concerns",
  "Parenting disagreements",
  "Pre-marital preparation",
];

const whatToExpect = [
  {
    num: "01",
    title: "A structured, goal-oriented approach",
    body: "Couples therapy isn't just venting. We use structured sessions to identify the real patterns causing problems, then build skills to change them. Every session has a purpose.",
    color: "#B4A6E3",
  },
  {
    num: "02",
    title: "Better communication, not just talking more",
    body: "Most couples don't need to talk more. They need to talk differently. We teach practical communication tools that help you express needs, listen better, and resolve conflict without escalation.",
    color: "#7D54FC",
  },
  {
    num: "03",
    title: "Rebuilding connection and trust",
    body: "Whether you're dealing with disconnection, betrayal, or simply growing apart, we work on rebuilding the foundation: trust, understanding, and genuine emotional intimacy.",
    color: "#3C6FF8",
  },
  {
    num: "04",
    title: "A neutral, skilled facilitator",
    body: "Our couples therapists are trained in Gottman-informed and evidence-based approaches. They stay neutral while guiding productive conversations that would otherwise derail at home.",
    color: "#6B742F",
  },
];

export default function CouplesTherapy() {
  return (
    <>
      <PageLayout>
        {/* Hero */}
        <section className="relative w-full min-h-[520px] md:min-h-[620px] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/images/couples/couples-hero.png"
              alt="Couples therapy at Perspective"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(28,20,12,0.48) 0%, rgba(42,30,18,0.32) 50%, rgba(60,40,20,0.14) 100%)" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(20,14,8,0.38) 0%, transparent 50%)" }} />
          </div>
          <div className="relative z-10 w-full px-6 md:px-12 lg:px-20 pt-36 pb-20">
            <div className="max-w-2xl">
              <p className="font-manrope text-white/90 text-[13px] font-semibold tracking-widest uppercase mb-4">
                Couples Therapy
              </p>
              <h1 className="font-fraunces text-white text-4xl md:text-5xl lg:text-[3.4rem] font-light leading-[1.15] mb-6">
                Work through it together{" "}
                <em className="not-italic font-normal" style={{ color: "#C9BCEE" }}>
                  with real support
                </em>
              </h1>
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-3 md:p-4 max-w-lg">
                <p className="font-manrope text-[#2B2B2B]/90 text-lg leading-relaxed">
                  Gottman-informed, evidence-based couples therapy for partners at every stage. Telehealth across NY, NJ, and Indiana. In-person in Larchmont.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* What we help with */}
        <section className="w-full py-20 md:py-28">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <ScrollReveal variant="fadeIn" delay={0}>
                  <div className="flex items-center gap-2 mb-4">
                    <div className="w-5 h-[2px] rounded-full" style={{ background: "linear-gradient(90deg, #6B742F, #7D54FC)" }} />
                    <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase" style={{ color: "#6B742F" }}>
                      What We Help With
                    </span>
                  </div>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={80}>
                  <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-6">
                    Every couple hits rough patches{" "}
                    <em className="not-italic" style={{ color: "#7D54FC" }}>the difference is what you do next</em>
                  </h2>
                </ScrollReveal>
                <ScrollReveal variant="fadeUp" delay={160}>
                  <p className="font-manrope text-[#2B2B2B]/82 text-[15px] leading-relaxed mb-8">
                    Whether you're dealing with constant arguments, emotional distance, trust issues, or just feeling like you're growing apart, couples therapy can help you understand what's really going on and learn how to fix it. Our Gottman-informed approach is practical, structured, and focused on building skills that last.
                  </p>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={240}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                    {issues.map((issue) => (
                      <div key={issue} className="flex items-center gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-sm" style={{ color: "#6B742F" }}></i>
                        </span>
                        <span className="font-manrope text-[14px] text-[#2B2B2B]/80">{issue}</span>
                      </div>
                    ))}
                  </div>
                </ScrollReveal>

                <ScrollReveal variant="fadeUp" delay={320}>
                  <div className="flex flex-col sm:flex-row gap-4">
                    <a
                      href="/therapists"
                      className="inline-flex items-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      style={{ background: "#6B742F" }}
                      onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#5a6328"}
                      onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#6B742F"}
                    >
                      Find a Couples Therapist <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="/contact"
                      className="inline-flex items-center gap-1.5 font-manrope text-[15px] font-medium text-[#2B2B2B]/78 hover:text-[#2B2B2B] transition-colors cursor-pointer whitespace-nowrap"
                    >
                      Learn more <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                </ScrollReveal>
              </div>
              <ScrollReveal variant="fadeRight" delay={0}>
                <div className="w-full h-[280px] sm:h-[340px] md:h-[500px] rounded-2xl overflow-hidden">
                  <img
                    src="/images/couples/couples-supporting.png"
                    alt="Couples therapy session"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* What to expect */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F8F5EE 0%, #F5F1E8 45%, #F2EDE4 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mb-14">
              <ScrollReveal variant="fadeIn" delay={0}>
                <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#6B742F" }}>
                  What to Expect
                </span>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Couples therapy that actually changes how you relate to each other
                </h2>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={160}>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">
                  Our couples therapists are trained in Gottman-informed methods and evidence-based approaches. Sessions are structured, practical, and designed to help you make real progress, not just talk in circles.
                </p>
              </ScrollReveal>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
              {whatToExpect.map((item, i) => (
                <ScrollReveal key={item.num} variant="fadeUp" delay={i * 90}>
                  <div className="rounded-2xl p-7 border bg-white/80" style={{ borderColor: item.color + "22" }}>
                    <div className="h-[3px] w-10 rounded-full mb-5" style={{ background: item.color }} />
                    <div className="flex items-start gap-4">
                      <span className="font-manrope text-sm font-bold shrink-0 mt-0.5" style={{ color: item.color }}>{item.num}</span>
                      <div>
                        <h3 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2">{item.title}</h3>
                        <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ-ish note */}
        <section className="w-full py-16 md:py-20">
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-3xl mx-auto">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="font-fraunces text-[#2B2B2B] text-2xl md:text-3xl font-light leading-snug mb-8 text-center">
                  Common questions about couples therapy
                </h2>
              </ScrollReveal>

              <div className="flex flex-col gap-6">
                {[
                  {
                    q: "Do both partners need to attend every session?",
                    a: "For the most part, yes. Couples therapy works best when both partners are present. However, there may be occasional individual sessions as part of the process.",
                  },
                  {
                    q: "How long are couples therapy sessions?",
                    a: "Couples sessions are 45-50 minutes. This gives enough time to address complex dynamics and work through meaningful conversations without rushing."
                  },
                  {
                    q: "Is it too late if we're already considering separation?",
                    a: "Not necessarily. Many couples find therapy helpful even when they're at a crossroads. It can help you make a clearer, more intentional decision, whether that means working through it or separating with mutual understanding.",
                  },
                ].map((faq, i) => (
                  <ScrollReveal key={faq.q} variant="fadeUp" delay={i * 80}>
                    <div className="rounded-2xl p-6 border bg-white/80" style={{ borderColor: "rgba(203,191,175,0.35)" }}>
                      <h4 className="font-fraunces text-[#2B2B2B] text-lg font-medium mb-2">{faq.q}</h4>
                      <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed">{faq.a}</p>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full py-20 md:py-28" style={{ background: "linear-gradient(180deg, #F2EEE4 0%, #EEE8DA 45%, #F5F1E8 100%)" }}>
          <div className="w-full px-6 md:px-12 lg:px-20">
            <div className="max-w-2xl mx-auto text-center">
              <ScrollReveal variant="fadeUp" delay={0}>
                <h2 className="font-fraunces text-[#2B2B2B] text-3xl md:text-4xl font-light leading-snug mb-5">
                  Couples therapy is $200 per session
                </h2>
                <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed mb-8 max-w-lg mx-auto">
                  45-50 minute joint sessions. Rates vary by therapist and are listed on each profile. Telehealth across NY, NJ, and Indiana. In-person available in Larchmont. Start with a free consultation.
                </p>
              </ScrollReveal>
              <ScrollReveal variant="fadeUp" delay={80}>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <a
                    href="/therapists"
                    className="inline-flex items-center justify-center gap-2 text-white font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
                    style={{ background: "#3C6FF8" }}
                    onMouseEnter={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"}
                    onMouseLeave={(e) => (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"}
                  >
                    Find Your Therapist <i className="ri-arrow-right-line"></i>
                  </a>
                  <a
                    href="/contact"
                    className="inline-flex items-center justify-center gap-2 font-manrope font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap border"
                    style={{ color: "#2B2B2B", borderColor: "rgba(43,43,43,0.18)" }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = "#2B2B2B";
                      (e.currentTarget as HTMLAnchorElement).style.color = "#fff";
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLAnchorElement).style.background = "transparent";
                      (e.currentTarget as HTMLAnchorElement).style.color = "#2B2B2B";
                    }}
                  >
                    Free Consultation
                  </a>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}